import { HeartAddIcon, FavouriteIcon } from "hugeicons-react";
import { useSaveJob } from "@/modules/hooks/job/useSaveJob";
import { useEmployeeInfo } from "@/modules/hooks/employee/useEmployeeInfo";
import { routes } from "@/lib/utils/routes";
import { useRouter } from "next/navigation";
import { useClientTranslations } from "@/lib/translate";
import CircularProgress from "../commons/CircularProgress";

interface SaveJobButtonProps {
  jobId: string;
  isSaved?: boolean;
  disabled?: boolean;
  className?: string;
  showText?: boolean;
}

export const SaveJobButton = ({
  jobId,
  isSaved = false,
  disabled = false,
  className = "",
  showText = false,
}: SaveJobButtonProps) => {
  const { t } = useClientTranslations("job-info");
  const router = useRouter();
  const { employeeInfo } = useEmployeeInfo();
  const { toggleSaveJob, isJobSaved, status } = useSaveJob();

  const handleSaveJob = async () => {
    if (!employeeInfo?.id) {
      router.push(routes.auth.login);
      return;
    }

    const result = await toggleSaveJob(jobId, employeeInfo.id);
    if (!result.success) {
      console.error("Failed to toggle save job:", result.error);
      // You might want to show a toast notification here
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Add a subtle scale animation on click
    const button = e.currentTarget;
    button.style.transform = "scale(0.95)";
    setTimeout(() => {
      button.style.transform = "scale(1)";
    }, 150);

    handleSaveJob();
  };

  const isCurrentlySaved = isJobSaved(jobId) || isSaved;
  const isLoading = status === "loading";

  return (
    <button
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`p-1 lg:px-5 py-3 h-full rounded-full flex flex-row items-center justify-center gap-2 lg:rounded-lg transition-all duration-300 min-w-fit ${
        disabled || isLoading
          ? "cursor-not-allowed bg-gray-100 text-gray-400 hover:bg-gray-100 opacity-60"
          : "cursor-pointer hover:bg-gray-200"
      } ${
        disabled || isLoading
          ? "text-gray-400"
          : isCurrentlySaved
          ? "text-red-500"
          : "text-primary"
      } ${className}`}
    >
      {isLoading ? (
        <CircularProgress className="h-4 w-4 text-primary" />
      ) : (
        <div className="relative w-6 h-6 flex items-center justify-center">
          <HeartAddIcon
            className={`absolute inset-0 transition-all duration-500 ease-out ${
              isCurrentlySaved
                ? "opacity-0 scale-50 rotate-45"
                : "opacity-100 scale-100 rotate-0"
            }`}
          />
          <FavouriteIcon
            className={`absolute inset-0 transition-all duration-500 ease-out ${
              isCurrentlySaved
                ? "opacity-100 scale-100 rotate-0"
                : "opacity-0 scale-50 -rotate-45"
            } fill-current`}
          />
        </div>
      )}
      {showText && (
        <span className="hidden lg:flex items-center align-middle">
          {isCurrentlySaved ? t("savedJob") : t("saveJob")}
        </span>
      )}
    </button>
  );
};
